import { Dispatch, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

export interface Association {
  id?: number;
  name: string;
  email?: string;
  phone?: string;
  meetingDate: Date;
  billDueDate: Date;
  basePay: number;
  unitPay: number;
  reminderFee: number;
  foreclosureFee: number;
  roadId: number;
  iban: string;
  bic: string;
}

export interface AssociationState {
  association: Association | undefined;
  associations: Association[];
  open: boolean;
  status: "idle" | "loading" | "failed";
}

const initialState: AssociationState = {
  association: undefined,
  associations: [],
  open: false,
  status: "idle",
};

export const fetchAssociations = createAsyncThunk(
  "assocications/fetchAssociations",
  async (associationId: number) => {
    const response = await axiosInstance(
      `/associations?associationId=${associationId}`
    );
    const data = await response.data;
    return data;
  }
);

export const fetchAssociation = createAsyncThunk(
  "assocications/fetchAssociation",
  async (associationId: number) => {
    const response = await axiosInstance(
      `/association?associationId=${associationId}`
    );
    const data = await response.data;
    return data;
  }
);

const associationSlice = createSlice({
  name: "association",
  initialState,
  reducers: {
    setAssociation(state, action) {
      state.association = action.payload;
    },
    addAssociation(state, action) {
      state.associations.push(action.payload);
    },
    updateAssociation(state, action) {
      const associationId = state.associations.findIndex(
        (r) => r.id === action.payload.id
      );
      state.associations[associationId] = action.payload;
    },
    deleteAssociation(state, action) {
      state.associations = state.associations.filter(
        (r: any) => r.id !== action.payload
      );
    },
    setOpen(state, action) {
      state.open = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssociations.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAssociations.fulfilled, (state, action) => {
        state.status = "idle";
        state.associations = action.payload;
      })
      .addCase(fetchAssociations.rejected, (state) => {
        state.status = "failed";
      });
    builder.addCase(fetchAssociation.fulfilled, (state, action) => {
      state.association = action.payload;
    });
  },
});

export function setAssociation(association: any) {
  return async (dispatch: Dispatch) => {
    dispatch(associationSlice.actions.setAssociation(association));
  };
}

export function createAssociation(association: any) {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axiosInstance.post(`/associations`, association);
      dispatch(associationSlice.actions.addAssociation(response.data));
    } catch (error: any) {
      throw error;
    }
  };
}

export function updateAssociation(id: number, association: any) {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axiosInstance.put(
        `/associations/${id}`,
        association
      );
      dispatch(associationSlice.actions.updateAssociation(response.data));
    } catch (error: any) {
      throw error;
    }
  };
}

export function deleteAssociation(id: number) {
  return async (dispatch: Dispatch) => {
    try {
      await axiosInstance.delete(`/associations/${id}`);
      dispatch(associationSlice.actions.deleteAssociation(id));
    } catch (error: any) {
      throw error;
    }
  };
}

export function openAssociationForm(open: boolean) {
  return async (dispatch: Dispatch) => {
    dispatch(associationSlice.actions.setOpen(open));
  };
}

export default associationSlice.reducer;
